// Broker-account context for the operator console (SM-786, epic
// SM-621 phase 09b).
//
// Every maker account executes under exactly ONE active broker account
// (today always its SYSTEM_BROKER account). The server stamps that id
// on the account rows and on every execution read; this module is the
// console's one statement of how it is used:
//
//   1. Cache scope — an account-scoped query is keyed by (user, broker
//      account) so a figure read for one broker account can never be
//      served for another from cache or placeholder data.
//   2. Read attribution — a balance read that names a DIFFERENT broker
//      account than the one the operator is looking at is not this
//      account's figure, and is treated as unreadable (which blocks its
//      money-moving action, exactly like any other unreadable balance).
//   3. Transfer assertions — the transfer body tells the server which
//      broker accounts the operator was shown; the server refuses on a
//      mismatch instead of moving money between accounts nobody saw.
//
// ABSENT IS PARITY. While FF_BROKER_ATTRIBUTION is off (or the broker
// read failed) no id is sent, every helper here degrades to exactly
// the pre-SM-786 behaviour: same query keys, same request body, no
// extra UI.
//
// Lives in lib/ with extensioned, type-only imports so the node test
// runner can execute it without the Vite alias table.

import type { MMAccountTransferRequest, MMMakerAccount } from "./types.ts";

type BrokerAccountFields = Pick<MMMakerAccount, "broker_account_id" | "broker_id">;

/**
 * The account slot of an account-scoped query key. Exactly the user id
 * when no broker account is known — so SYSTEM_BROKER / flag-off keys
 * are byte-identical to the keys before SM-786 — and `user@account`
 * when one is, which keeps every key the same LENGTH (the
 * keepWithinScope scope lengths and every prefix invalidation keep
 * working) while making two broker accounts two cache entries.
 */
export function accountScope(userID: string, brokerAccountID?: string): string {
  return brokerAccountID ? `${userID}@${brokerAccountID}` : userID;
}

/**
 * A compact label for a table cell: `bac_…a1b2c3`. Broker account ids
 * are prefix + ULID, and a ULID's LEADING characters are its timestamp
 * — two accounts created the same day share them — so the tail is what
 * tells two accounts apart. Undefined when there is no id to show.
 */
export function shortBrokerAccount(id: string | undefined, keep = 6): string | undefined {
  if (!id) return undefined;
  const sep = id.indexOf("_");
  const prefix = sep > 0 ? id.slice(0, sep + 1) : "";
  const body = sep > 0 ? id.slice(sep + 1) : id;
  if (body.length <= keep + 1) return id;
  return `${prefix}…${body.slice(-keep)}`;
}

/**
 * Whether a read was made for a DIFFERENT broker account than the one
 * shown. Only a positive disagreement counts: either side absent is
 * "not attributed" (flag off, older server), which is today's
 * behaviour and must not block anything.
 */
export function brokerAccountMismatch(shown: string | undefined, readFor: string | undefined): boolean {
  return !!shown && !!readFor && shown !== readFor;
}

/**
 * The balance rows of a read, or `undefined` when that read belongs to
 * another broker account. `undefined` is the console's "could not read"
 * — never an empty list the caller could mistake for "no wallets" —
 * so every guard downstream already treats it as blocking.
 */
export function attributedRows<R>(
  shownBrokerAccountID: string | undefined,
  read: { broker_account_id?: string; balances?: R[] } | undefined,
): R[] | undefined {
  if (!read) return undefined;
  if (brokerAccountMismatch(shownBrokerAccountID, read.broker_account_id)) return undefined;
  return read.balances;
}

/**
 * Whether two accounts sit under different brokers. A transfer cannot
 * cross brokers; the server enforces it (403 BROKER_SCOPE_DENIED), the
 * console just refuses to offer it. Unknown on either side is not a
 * crossing — the server remains the authority.
 */
export function crossesBrokers(
  a: BrokerAccountFields | undefined,
  b: BrokerAccountFields | undefined,
): boolean {
  return !!a?.broker_id && !!b?.broker_id && a.broker_id !== b.broker_id;
}

export interface TransferBodyInput {
  source: Pick<MMMakerAccount, "user_id" | "broker_account_id">;
  destination: Pick<MMMakerAccount, "user_id" | "broker_account_id">;
  amountMinor: number;
  reason: string;
  idempotencyKey: string;
  currency: string;
}

/**
 * The transfer request body. The broker-account assertions are
 * included ONLY when the account rows carried them: an empty string
 * would be an assertion of "no account", and leaving them off keeps the
 * flag-off body identical to the pre-SM-786 one.
 */
export function buildTransferRequest(input: TransferBodyInput): MMAccountTransferRequest {
  const body: MMAccountTransferRequest = {
    source_user_id: input.source.user_id,
    destination_user_id: input.destination.user_id,
    amount_minor: input.amountMinor,
    reason: input.reason,
    idempotency_key: input.idempotencyKey,
    currency: input.currency,
  };
  if (input.source.broker_account_id) body.source_broker_account_id = input.source.broker_account_id;
  if (input.destination.broker_account_id) {
    body.destination_broker_account_id = input.destination.broker_account_id;
  }
  return body;
}

/** The two broker-account refusals a money move can meet. */
export type BrokerRefusal = "stale" | "scope";

export const BROKER_SCOPE_DENIED = "BROKER_SCOPE_DENIED";

/**
 * Classify a failed money move. Duck-typed on {status, code, message}
 * (the APIError shape) so it runs without the fetch layer.
 *
 *   - "scope": 403 BROKER_SCOPE_DENIED — the accounts are under
 *     different brokers (transfer) or the execution user is not a
 *     SYSTEM_BROKER account (fund / return). Retrying cannot help.
 *   - "stale": 409 naming the broker account — what the operator was
 *     shown is no longer the user's account. A refresh, then a NEW
 *     confirmation, is the only way forward.
 */
export function classifyBrokerRefusal(err: unknown): BrokerRefusal | undefined {
  if (!err || typeof err !== "object") return undefined;
  const e = err as { status?: unknown; code?: unknown; message?: unknown };
  const code = typeof e.code === "string" ? e.code : "";
  const message = typeof e.message === "string" ? e.message : "";
  if (code === BROKER_SCOPE_DENIED || message.includes(BROKER_SCOPE_DENIED)) return "scope";
  if (e.status === 409 && (/broker account/i.test(message) || /BROKER_ACCOUNT_STALE/.test(code))) {
    return "stale";
  }
  return undefined;
}
